// The commissioned encounter terrain on the board (docs/186 EA1–EA5): a painted board (`paintBoard`) drawn as
//   ground    the flat picture, baked into chunks fed straight from its pixels (DataTexture, one per 512 px
//             chunk cropped to its opaque pixels, culled to the view as BakedChunks' are), at the bottom of the
//             `terrain` layer where the Oryx stamps went, so the light map, weather, liquids, fog and matter
//             draw over it as before;
//   lit       a night set's lit pixels (docs/164 ET9), baked the same way into a layer just above the light map,
//             shown only while the lighting makes the board dark, so torches and lit windows stay bright;
//   standing  every wall, tree, thicket, cover and big piece as an image in the `units` layer at depth `foot`,
//             so it sorts with the units: a figure behind a wall or a tree is hidden by it, one in front is drawn
//             over it. Raised wall squares (the painter's own pixels) are packed into a few DataTexture atlases;
//             pieces are frames of the set's own texture. They are above the light map, so each is tinted from
//             it at its four corners (no unit floor), and from the fog of war: hidden where never seen, darkened
//             as the fog's memory veil where explored and out of sight. Tints change only when the light map is
//             rebuilt or the fog changes, and at 4 Hz while flames flicker; only images in view are tinted, the
//             rest when they come into view;
//   lit standing  (docs/186, performance) for each standing image with lit pixels, an untinted image of only those pixels
//             just in front of it (depth foot + 0.001), shown while the board is dark, hidden and veiled by the
//             fog as its image is: lit windows, lanterns, braziers and wall torches stay bright at night;
//   see-through  (EA12) a standing image (and its lit pixels) fades to FADE_ALPHA while it hides a figure drawn on this
//             client or the square the local pointer is on (`seeThrough`, the pure part in `seeThrough.ts`).
// The board is painted and cut in a Web Worker (`painter.ts`, docs/186, performance) while the set loads here; until
// both land the board keeps its Oryx look (TileRenderer), and a board that changes keeps its last painting
// until the new one is ready. `?oryx=1` keeps the Oryx look for good (before/after comparisons).
import Phaser from 'phaser';
import type { FogView } from '../../core/sim/types';
import { DataTexture } from '../board/dataTexture';
import { CHUNK } from '../board/BakedChunks';
import { sameValue } from '../board/sameValue';
import { loadSet, registerLitTexture, registerSetTexture, releaseSetTexture } from './atlas';
import type { BoardIn } from './paint';
import { paintBoardAsync, paintNow, painterWorks } from './painter';
import type { Prepared, ShownBoard } from './prepare';
import { FADE_ALPHA, PieceGrid, opaqueSums, stepFade, type SeeTarget } from './seeThrough';
import type { EncounterSet, Standing } from './types';

type RGB = readonly [number, number, number];

/** `?oryx=1`: every board keeps its Oryx look (before/after comparisons). */
export function oryxForced(): boolean {
  try { return new URLSearchParams(window.location.search).get('oryx') === '1'; } catch { return false; }
}

/** What the standing images read from the lighting (LightingLayer). */
export interface TerrainLight {
  /** The light map at a board point in squares; undefined when lighting is off. */
  lightAt(cx: number, cy: number): RGB | undefined;
  /** Changes whenever the light map is rebuilt (board, hour, weather, mode). */
  readonly version: number;
  /** The board is dark enough for the lit pixels to matter (night, underground). */
  readonly dark: boolean;
  /** Flames flicker on the board (and motion is not reduced): re-tint now and then. */
  readonly flickering: boolean;
}

/** The fog's memory veil, as the fog shader applies it to explored squares out of sight (fogShader.ts). */
export interface FogVeil { memory: RGB; memWash: number; memDark: number; strength: number }

/** A standing image's opaque pixels, for the wildlife's occluders. */
export interface StandingMask { x: number; y: number; w: number; h: number; foot: number; alpha: Uint8Array }

export interface TerrainInfo {
  set?: string; standing: number; made: number; parts: number; partsMade: number; walls: number; pieces: number; atlases: number; chunks: number; lit: number; uploaded: number; litStanding: number;
  paintMs: number; prepMs: number; uploadMs: number; worker: boolean; times: Record<string, number>; headroom: number; hidden: number; tinted: number; inView: number; loading: string[];
}

/** A painted board ready to show in its set; `key` changes with every new painting. */
export interface ShownArt { set: EncounterSet; prepared: Prepared; key: string }

/** A chunk of a picture; its texture and image are made the first time it is wanted (in view, or idle time). */
interface Chunk { x: number; y: number; w: number; h: number; data: Uint8ClampedArray; layer: Phaser.GameObjects.Layer; tag: string; bottom: boolean; img?: Phaser.GameObjects.Image; tex?: DataTexture }
interface Item { i: number; s: Standing; img: Phaser.GameObjects.Image; lit?: Phaser.GameObjects.Image; inView: boolean; hidden: boolean; fog?: RGB; tinted: number }
/** A region's standing images and atlases, made the first time the region is wanted (docs/186, performance). */
interface Part { x0: number; y0: number; x1: number; y1: number; idx: readonly number[]; items?: Item[] }
interface Job { id: string; board: BoardIn; serial: number; shown?: ShownArt }

/** Re-tint for flicker at most this often (ms). */
const FLICKER_MS = 250;
/** A mid-tone the fog's wash is measured against when it is turned into a multiply tint. */
const MID = 0.45;
/** In front of its image, behind anything standing on the next row. */
const LIT_DEPTH = 0.001;

let serial = 0;

const hex = (r: number, g: number, b: number): number => (Math.round(Math.max(0, Math.min(1, r)) * 255) << 16) | (Math.round(Math.max(0, Math.min(1, g)) * 255) << 8) | Math.round(Math.max(0, Math.min(1, b)) * 255);

export class EncounterTerrain {
  private readonly id = `fk-enc-${serial++}`;
  private sets = new Map<string, EncounterSet>();
  private loading = new Set<string>();
  private failed = new Set<string>();
  /** Whether a set could not be loaded or painted (the board keeps the Oryx look). */
  hasFailed(id: string): boolean { return this.failed.has(id); }
  private ground: Chunk[] = [];
  private lit: Chunk[] = [];
  private atlases: DataTexture[] = [];
  private items: Item[] = [];
  private parts: Part[] = [];
  private view: Item[] = [];
  /** The fog as last set, for images made later: per square 0 unseen, 1 remembered, 2 seen. */
  private fogNow?: { cells: ArrayLike<number>; w: number; mem?: RGB };
  private setKey = '';
  private litSet?: { key: string; frames: ReadonlySet<string> };
  private tintSerial = 1;
  private light?: TerrainLight;
  private lightVersion = -1;
  private lastTint = -1;
  private fogKey = '';
  private camKey = '';
  private litShown = false;
  private frames = new Map<string, Uint8ClampedArray>();
  /** Ground chunks made so far (they sit at the bottom of the layer, in the order made). */
  private groundMade = 0;
  private madeThisFrame = false;
  private stats = { paintMs: 0, prepMs: 0, uploadMs: 0 };
  /** When (performance.now) the art was first asked for, its set loaded, its board painted and shown: for drivers. */
  private times: Record<string, number> = {};
  /** The board being painted (or painted last) and what came of it. */
  private job?: Job;
  /** The last painting ready to show, kept on show while a changed board paints. */
  private last?: ShownArt;
  private dead = false;
  /** The painted board on show (undefined: the Oryx look). Its pictures live in the chunks. */
  painted?: ShownBoard;
  /** The set it was painted in. */
  set?: EncounterSet;
  /** The painting on show. */
  private shown?: Prepared;
  /** EA12: the standing pieces by bucket, made the first time something must show through. */
  private grid?: PieceGrid;
  /** EA12: opaque-pixel sums per piece (a set's frame once per flip, a wall square once). */
  private sums = new Map<string, Uint32Array>();
  /** EA12: the made images by standing index. */
  private byIndex = new Map<number, Item>();
  /** EA12: the pieces hiding something now, and every piece not at full alpha (its alpha). */
  private hidingNow = new Set<number>();
  private faded = new Map<number, number>();
  private seeKey = '';
  private seeTime = -1;

  constructor(private readonly scene: Phaser.Scene, private readonly layers: { ground: Phaser.GameObjects.Layer; lit: Phaser.GameObjects.Layer; units: Phaser.GameObjects.Layer }, private readonly onReady: () => void) {
    scene.events.on(Phaser.Scenes.Events.PRE_RENDER, this.cull);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  /**
   * `board` painted in set `id`, if both are ready; otherwise starts what is missing (the set's load here, the
   * painting in the worker), calls `onReady` once both have landed, and meanwhile gives the last painting in
   * the same set (a board that changed keeps its art until the new one is painted) or undefined (the Oryx look).
   */
  ready(id: string, board: BoardIn): ShownArt | undefined {
    const set = this.setOf(id);
    if (this.failed.has(id)) return undefined;
    let job = this.job;
    if (!job || job.id !== id || !sameValue(job.board, groundOf(board))) {
      job = this.job = { id, board: structuredClone(groundOf(board)), serial: serial++ };
      this.times.asked ??= performance.now();
      if (painterWorks()) {
        const mine = job;
        paintBoardAsync(mine.board, id, CHUNK).then((prepared) => {
          if (this.dead || this.job !== mine || !prepared) return;
          this.times.painted ??= performance.now();
          mine.shown = { set: undefined as unknown as EncounterSet, prepared, key: `${id}#${mine.serial}` };
          if (this.sets.has(id)) this.onReady();
        }, (e: unknown) => {
          if (this.dead || this.job !== mine) return;
          this.failed.add(id);
          console.warn(`encounter art: could not paint in ${id}; keeping the Oryx look`, e);
        });
      }
    }
    if (!set) return undefined;
    // no worker (tests, old engines): painted here, as soon as the set is in
    if (!job.shown && !painterWorks()) job.shown = { set, prepared: paintNow(job.board, set, CHUNK), key: `${id}#${job.serial}` };
    if (job.shown) { job.shown.set = set; this.last = job.shown; return job.shown; }
    return this.last && this.last.set.id === id ? this.last : undefined;
  }

  /** The set `id` if it has loaded; otherwise starts loading it (once) and calls `onReady` when it lands (and its board is painted). */
  private setOf(id: string): EncounterSet | undefined {
    const have = this.sets.get(id);
    if (have) return have;
    if (this.failed.has(id) || this.loading.has(id)) return undefined;
    this.loading.add(id);
    loadSet(id).then((set) => {
      this.loading.delete(id);
      if (this.dead || (!this.scene.sys?.isActive() && !this.scene.sys?.isPaused())) return;
      registerSetTexture(this.scene, set, this);
      this.times.loaded ??= performance.now();
      this.sets.set(id, set);
      // only the sets this scene has asked for most recently stay (the loader keeps three too); the others'
      // textures go (docs/186, performance), never the one on show
      for (const old of this.sets.keys()) {
        if (this.sets.size <= 3) break;
        if (old === this.set?.id || old === id) continue;
        this.sets.delete(old);
        releaseSetTexture(this.scene, old, this);
      }
      if (this.job?.id === id && (this.job.shown || !painterWorks())) this.onReady();
    }, (e: unknown) => {
      this.loading.delete(id);
      this.failed.add(id);
      console.warn(`encounter art: could not load ${id}; keeping the Oryx look`, e);
    });
    return undefined;
  }

  /** Shows a painting (from `ready`): ground, lit pixels, standing images. The same painting again is kept as it is. */
  show(art: ShownArt): ShownBoard {
    if (this.shown === art.prepared && this.painted) return this.painted;
    this.clear();
    const t0 = performance.now();
    const { prepared, set } = art;
    this.shown = prepared;
    this.painted = prepared.board; this.set = set;
    this.ground = prepared.ground.map((c) => ({ ...c, layer: this.layers.ground, tag: 'g', bottom: true }));
    this.lit = prepared.lit.map((c) => ({ ...c, layer: this.layers.lit, tag: 'l', bottom: false }));
    this.groundMade = 0;
    this.litShown = true;
    this.frames.clear();
    this.prepareStanding(prepared, set);
    this.stats = { paintMs: prepared.paintMs, prepMs: prepared.prepMs, uploadMs: performance.now() - t0 };
    this.times.shown ??= performance.now();
    this.lightVersion = -1; this.fogKey = ''; this.camKey = '';
    return prepared.board;
  }

  /** Back to no art (the Oryx look). */
  clear(): void {
    for (const c of [...this.ground, ...this.lit]) { c.img?.destroy(); c.tex?.destroy(); }
    for (const it of this.items) { it.img.destroy(); it.lit?.destroy(); }
    this.parts = [];
    this.grid = undefined; this.sums.clear(); this.byIndex.clear(); this.hidingNow.clear(); this.faded.clear(); this.seeKey = '';
    for (const a of this.atlases) a.destroy();
    this.ground = []; this.lit = []; this.items = []; this.atlases = []; this.view = []; this.litSet = undefined;
    this.painted = undefined; this.set = undefined; this.shown = undefined;
    this.frames.clear();
  }

  /** The lighting the standing images are tinted from (and that shows the lit pixels). */
  setLighting(light: TerrainLight | undefined): void { this.light = light; this.lightVersion = -1; }

  /** docs/31: the fog of war over the standing images (undefined: a clear field). */
  setFog(fog: FogView | undefined, veil: FogVeil | undefined): void {
    const p = this.painted;
    const ok = !!p && !!fog && fog.w === p.w && fog.h === p.h && fog.cells.length === p.w * p.h;
    const key = ok ? `${fog!.cells.join('')}|${veil ? `${veil.memory.join(',')}:${veil.memWash}:${veil.memDark}:${veil.strength}` : ''}` : '';
    if (key === this.fogKey) return;
    this.fogKey = key;
    let mem: RGB | undefined;
    if (veil) {
      // the shader's memory: toward(memory, wash) then darken(dark); as a multiply on a mid-tone surface
      const wash = veil.memWash * veil.strength, dark = veil.memDark * veil.strength;
      mem = [0, 1, 2].map((c) => Math.min(1, (1 - wash + (veil.memory[c]! * wash) / MID) * (1 - dark))) as unknown as RGB;
    }
    this.fogNow = ok ? { cells: fog!.cells, w: fog!.w, ...(mem ? { mem } : {}) } : undefined;
    for (const it of this.items) this.fogOne(it);
    for (const it of this.items) this.applyOne(it);
    this.tintSerial++;
    this.tintView();
  }

  /** Per frame: re-tint when the light map was rebuilt, and now and then while flames flicker. */
  update(time: number): void {
    this.trickle();
    if (!this.items.length && !this.lit.length) return;
    const l = this.light;
    const shown = !!l && l.dark;
    if (shown !== this.litShown) {
      this.litShown = shown;
      if (!shown) for (const c of this.lit) c.img?.setVisible(false);
      for (const it of this.view) this.applyOne(it);
      this.camKey = '';
    }
    if (!this.items.length) return;
    const version = l?.version ?? -2;
    if (version !== this.lightVersion) { this.lightVersion = version; this.lastTint = time; this.tintSerial++; this.tintView(); return; }
    if (l?.flickering && time - this.lastTint >= FLICKER_MS) { this.lastTint = time; this.tintView(true); }
  }

  /**
   * EA12: per frame, what must show through the standing pieces: the figures drawn on this client (their drawn
   * rects and depths; never a foe the fog hides, as it is not drawn) and the local pointer's square. The pieces hiding
   * any of them fade to FADE_ALPHA over FADE_MS and come back as they stop. Local render state only.
   */
  seeThrough(targets: readonly SeeTarget[], time: number): void {
    const p = this.painted;
    const dt = this.seeTime < 0 ? 0 : Math.min(100, Math.max(0, time - this.seeTime));
    this.seeTime = time;
    if (!p) return;
    let key = '';
    for (const t of targets) key += `${Math.round(t.x)},${Math.round(t.y)},${Math.round(t.w)},${Math.round(t.h)},${Math.round(t.depth)},${t.cover};`;
    if (key !== this.seeKey) {
      this.seeKey = key;
      const grid = (this.grid ??= new PieceGrid(p.standing));
      const next = new Set<number>();
      for (const t of targets) grid.hiding(t, this.sumsOf, next);
      for (const i of next) if (!this.faded.has(i)) this.faded.set(i, 1);
      this.hidingNow = next;
    }
    for (const [i, a] of this.faded) {
      const to = this.hidingNow.has(i) ? FADE_ALPHA : 1;
      const b = dt ? stepFade(a, to, dt) : a;
      if (b === 1 && to === 1) this.faded.delete(i); else this.faded.set(i, b);
      if (b !== a || b === 1) this.alphaOne(i, b);
    }
  }

  /** A piece's opaque-pixel sums (undefined: its pixels are not here, so its whole rect counts). */
  private readonly sumsOf = (i: number): Uint32Array | undefined => {
    const s = this.painted?.standing[i];
    if (!s) return undefined;
    const key = s.px ? `#${i}` : `${s.frame ?? ''}|${s.flipX ? 1 : 0}`;
    let out = this.sums.get(key);
    if (out) return out;
    const px = s.px ?? this.frames.get(s.frame ?? '');
    if (!px || px.length < s.w * s.h * 4) return undefined;
    out = opaqueSums(px, s.w, s.h, !!s.flipX);
    this.sums.set(key, out);
    return out;
  };

  private alphaOne(i: number, a: number): void {
    const it = this.byIndex.get(i);
    if (!it) return;
    if (it.img.alpha !== a) it.img.setAlpha(a);
    if (it.lit && it.lit.alpha !== a) it.lit.setAlpha(a);
  }

  /** EA12: the pieces faded now and their alpha (for drivers). */
  seeThroughInfo(): Array<{ i: number; kind: string; at: { x: number; y: number }; alpha: number }> {
    return [...this.faded].map(([i, alpha]) => { const s = this.painted!.standing[i]!; return { i, kind: s.kind, at: { ...s.at }, alpha: Math.round(alpha * 100) / 100 }; });
  }

  /** What stands up off the board, with its opaque pixels (the wildlife hides behind them). */
  occluders(): StandingMask[] {
    const out: StandingMask[] = [];
    for (const s of this.painted?.standing ?? []) {
      const px = s.px ?? this.frames.get(s.frame ?? '');
      if (!px) continue;
      const alpha = new Uint8Array(s.w * s.h);
      for (let y = 0; y < s.h; y++) for (let x = 0; x < s.w; x++) {
        const sx = s.flipX ? s.w - 1 - x : x;
        alpha[y * s.w + x] = px[(y * s.w + sx) * 4 + 3]!;
      }
      out.push({ x: s.x, y: s.y, w: s.w, h: s.h, foot: s.foot, alpha });
    }
    return out;
  }

  /** Board px the standing images rise above row 0 (0 with no art). */
  get headroom(): number { return this.painted?.headroom ?? 0; }

  info(): TerrainInfo {
    return {
      ...(this.set ? { set: this.set.id } : {}),
      standing: this.painted?.standing.length ?? 0, walls: this.painted?.standing.filter((s) => s.px).length ?? 0, pieces: this.painted?.standing.filter((s) => !s.px).length ?? 0,
      made: this.items.length, parts: this.parts.length, partsMade: this.parts.filter((p) => p.items).length,
      atlases: this.atlases.length, chunks: this.ground.length, lit: this.lit.length, uploaded: [...this.ground, ...this.lit].filter((c) => c.tex).length, litStanding: this.items.filter((i) => i.lit).length,
      paintMs: Math.round(this.stats.paintMs * 10) / 10, prepMs: Math.round(this.stats.prepMs * 10) / 10, uploadMs: Math.round(this.stats.uploadMs * 10) / 10,
      worker: painterWorks(), headroom: this.headroom, times: Object.fromEntries(Object.entries(this.times).map(([k, v]) => [k, Math.round(v)])),
      hidden: this.items.filter((i) => i.hidden).length, tinted: this.items.filter((i) => i.img.isTinted).length, inView: this.view.length, loading: [...this.loading],
    };
  }

  // ---- build ------------------------------------------------------------------------------------

  /**
   * A chunk's texture and image, made when first wanted (docs/186, performance): each upload waits on the GPU (~5 ms a
   * 512 px chunk under SwiftShader, and every chunk at once was ~300 ms in one frame), so the board's art lands
   * with only what is in view, and the rest is uploaded a chunk a frame after (`trickle`) or when the camera
   * reaches it. Ground chunks go under whatever the layer holds (where the Oryx stamps went).
   */
  private make(c: Chunk): Phaser.GameObjects.Image {
    if (c.img) return c.img;
    c.tex = new DataTexture(this.scene, `${this.id}-${c.tag}-${c.x}-${c.y}-${serial++}`, c.w, c.h, c.data, false, true);
    c.img = this.scene.add.image(c.x, c.y, c.tex.key).setOrigin(0, 0);
    if (c.bottom) c.layer.addAt(c.img, this.groundMade++); else c.layer.add(c.img);
    this.madeThisFrame = true;
    return c.img;
  }

  /** One chunk not yet uploaded (ground first; lit pixels only while they show), when this frame made none. */
  private trickle(): void {
    if (this.madeThisFrame) { this.madeThisFrame = false; return; }
    const c = this.ground.find((g) => !g.img) ?? (this.litShown ? this.lit.find((g) => !g.img) : undefined);
    const part = c ? undefined : this.parts.find((p) => !p.items);
    if (!c && !part) return;
    if (c) this.make(c).setVisible(false);
    else this.makePart(part!);
    // shown by the cull if it is in view
    this.camKey = '';
    this.madeThisFrame = false;
  }

  /** What every region's images will need: the set's texture and lit frames, the pieces' pixels (occluders). */
  private prepareStanding(p: Prepared, set: EncounterSet): void {
    for (const list of set.pieces.values()) for (const piece of list) this.frames.set(piece.frame, piece.px);
    this.setKey = set.texture || registerSetTexture(this.scene, set, this);
    this.litSet = registerLitTexture(this.scene, set);
    this.parts = p.regions.filter((r) => r.items.length).map((r) => ({ x0: r.x0, y0: r.y0, x1: r.x1, y1: r.y1, idx: r.items }));
  }

  /** A region's atlases uploaded and its images made (hidden: the cull shows what is in view). */
  private makePart(part: Part): Item[] {
    if (part.items) return part.items;
    const p = this.shown!, standing = p.board.standing;
    const keys = new Map<number, string>();
    const sheet = (list: Prepared['sheets'], at: number, tag: string): string => {
      const k = keys.get(at * 2 + (tag === 'wl' ? 1 : 0));
      if (k) return k;
      const sh = list[at]!;
      const tex = new DataTexture(this.scene, `${this.id}-${tag}-${at}-${serial++}`, sh.w, sh.h, sh.data, false, true);
      this.atlases.push(tex);
      keys.set(at * 2 + (tag === 'wl' ? 1 : 0), tex.key);
      return tex.key;
    };
    const setTex = this.scene.textures.get(this.setKey);
    const items: Item[] = [];
    for (const i of part.idx) {
      const s = standing[i]!, pl = p.place[i];
      let img: Phaser.GameObjects.Image, lit: Phaser.GameObjects.Image | undefined;
      const ix = s.x + s.w / 2, f = `w${i}`;
      if (pl) {
        const key = sheet(p.sheets, pl.sheet, 'w');
        this.scene.textures.get(key).add(f, 0, pl.x, pl.y, s.w, s.h);
        img = this.scene.add.image(ix, s.y, key, f);
        if (pl.lit) {
          const lk = sheet(p.litSheets, pl.lit.sheet, 'wl');
          this.scene.textures.get(lk).add(f, 0, pl.lit.x, pl.lit.y, s.w, s.h);
          lit = this.scene.add.image(ix, s.y, lk, f);
        }
      } else if (s.frame && setTex.has(s.frame)) {
        img = this.scene.add.image(ix, s.y, this.setKey, s.frame);
        if (this.litSet?.frames.has(s.frame)) lit = this.scene.add.image(ix, s.y, this.litSet.key, s.frame);
      } else continue;
      // origin at the top middle: a flipped piece mirrors in place
      for (const o of lit ? [img, lit] : [img]) {
        o.setOrigin(0.5, 0).setDepth(o === lit ? s.foot + LIT_DEPTH : s.foot).setVisible(false);
        if (s.flipX) o.setFlipX(true);
        this.layers.units.add(o);
      }
      const it: Item = { i, s, img, ...(lit ? { lit } : {}), inView: false, hidden: false, tinted: 0 };
      this.fogOne(it);
      items.push(it);
      this.items.push(it);
      this.byIndex.set(i, it);
      const a = this.faded.get(i);
      if (a !== undefined) this.alphaOne(i, a);
    }
    part.items = items;
    this.madeThisFrame = true;
    return items;
  }

  /** An image's fog from the fog as last set. */
  private fogOne(it: Item): void {
    const f = this.fogNow;
    const v = f ? f.cells[it.s.at.y * f.w + it.s.at.x] : 2;
    // never seen: the fog there is opaque (and under a blind fog there is no board at all), so nothing stands
    it.hidden = v === 0;
    it.fog = v === 1 ? f?.mem : undefined;
  }

  // ---- per frame --------------------------------------------------------------------------------

  /**
   * Only what the camera shows is drawn (docs/147 PG9): chunks and standing images out of view are hidden.
   * Standing images are found through the bucket grid, so a pan touches only what is near the view.
   */
  private readonly cull = (): void => {
    const cam = this.scene.cameras?.main;
    if (!cam || (!this.parts.length && !this.ground.length)) return;
    const all = cam.shakeEffect.isRunning || (cam as unknown as { rotation: number }).rotation !== 0;
    const ox = cam.width * cam.originX, oy = cam.height * cam.originY, m = 2 / cam.zoomX;
    const x0 = cam.scrollX + ox - ox / cam.zoomX - m, y0 = cam.scrollY + oy - oy / cam.zoomY - m;
    const x1 = x0 + cam.width / cam.zoomX + 2 * m, y1 = y0 + cam.height / cam.zoomY + 2 * m;
    const key = all ? 'all' : `${Math.floor(x0)},${Math.floor(y0)},${Math.ceil(x1)},${Math.ceil(y1)}`;
    if (key === this.camKey) return;
    this.camKey = key;
    const seen = (x: number, y: number, w: number, h: number) => all || (x < x1 && x + w > x0 && y < y1 && y + h > y0);
    for (const c of this.ground) { const v = seen(c.x, c.y, c.w, c.h); if (v) this.make(c).setVisible(true); else c.img?.setVisible(false); }
    for (const c of this.lit) { const v = this.litShown && seen(c.x, c.y, c.w, c.h); if (v) this.make(c).setVisible(true); else c.img?.setVisible(false); }
    const next: Item[] = [];
    for (const part of this.parts) {
      if (!seen(part.x0, part.y0, part.x1 - part.x0, part.y1 - part.y0)) continue;
      for (const it of this.makePart(part)) if (seen(it.s.x, it.s.y, it.s.w, it.s.h)) next.push(it);
    }
    for (const it of this.view) it.inView = false;
    for (const it of next) it.inView = true;
    for (const it of this.view) if (!it.inView) this.applyOne(it);
    for (const it of next) {
      this.applyOne(it);
      if (it.tinted !== this.tintSerial) this.tintOne(it);
    }
    this.view = next;
  };

  private applyOne(it: Item): void {
    const v = it.inView && !it.hidden;
    if (it.img.visible !== v) it.img.setVisible(v);
    if (it.lit) { const lv = v && this.litShown; if (it.lit.visible !== lv) it.lit.setVisible(lv); }
  }

  /** Re-tints what is in view (`visibleOnly`: and showing); the rest is tinted as it comes into view. */
  private tintView(visibleOnly = false): void {
    for (const it of this.view) if (!visibleOnly || it.img.visible) this.tintOne(it);
  }

  /** An image's four corners from the light map (clamped into the board), times the fog's veil; its lit pixels take the veil only. */
  private tintOne(it: Item): void {
    const p = this.painted, l = this.light;
    if (!p) return;
    it.tinted = this.tintSerial;
    const c = p.cell, maxX = p.w - 0.01, maxY = p.h - 0.01;
    const at = (x: number, y: number): RGB | undefined => l?.lightAt(Math.max(0, Math.min(maxX, x / c)), Math.max(0, Math.min(maxY, y / c)));
    const s = it.s, f = it.fog;
    if (it.lit) { if (f) it.lit.setTint(hex(f[0], f[1], f[2])); else if (it.lit.isTinted) it.lit.clearTint(); }
    const x0 = s.x + 1, x1 = s.x + s.w - 1, y0 = s.y + 1, y1 = s.y + s.h - 1;
    const corners = [at(x0, y0), at(x1, y0), at(x0, y1), at(x1, y1)];
    if (!corners[0] && !f) { if (it.img.isTinted) it.img.clearTint(); return; }
    const k = corners.map((rgb) => hex((rgb?.[0] ?? 1) * (f?.[0] ?? 1), (rgb?.[1] ?? 1) * (f?.[1] ?? 1), (rgb?.[2] ?? 1) * (f?.[2] ?? 1)));
    // flipped, the frame's corners are drawn mirrored: the tint follows the board, not the frame
    if (s.flipX) it.img.setTint(k[1], k[0], k[3], k[2]);
    else it.img.setTint(k[0], k[1], k[2], k[3]);
  }

  destroy(): void {
    this.dead = true;
    this.clear();
    for (const id of this.sets.keys()) releaseSetTexture(this.scene, id, this);
    this.sets.clear();
    this.job = undefined; this.last = undefined;
    this.scene.events?.off(Phaser.Scenes.Events.PRE_RENDER, this.cull);
  }
}

/** What the painter reads of a board (as TileRenderer compares grounds). */
const groundOf = (b: BoardIn): BoardIn => ({ w: b.w, h: b.h, tiles: b.tiles, ...(b.seed !== undefined ? { seed: b.seed } : {}), ...(b.country ? { country: b.country } : {}), ...(b.look ? { look: b.look } : {}) });
